<script lang="ts">
  import type { HeatmapDay } from './member';
  import { HEAT, heatLevel } from './heat';
  import RoleBreakdown from './RoleBreakdown.svelte';
  import ChartTooltip from './ChartTooltip.svelte';
  import ActivityBreakdown from './ActivityBreakdown.svelte';

  // Cells are responsive: week columns share the container width equally (capped
  // so they don't balloon when there are few weeks) and cells are square via
  // aspect-ratio. `gap` is the px gutter; `showLabels`/`months` toggle the
  // weekday column and the month row. `detailed` swaps the native cell title for a
  // rich fixed hover tooltip (day totals + per-contributor-role breakdown when the
  // day carries `roles`) — used on the community activity heatmap.
  let {
    days,
    max,
    months = true,
    gap = 3,
    showLabels = true,
    detailed = false,
    onpick = undefined,
  }: {
    days: HeatmapDay[];
    max: number;
    months?: boolean;
    gap?: number;
    showLabels?: boolean;
    detailed?: boolean;
    onpick?: ((date: string) => void) | undefined;
  } = $props();

  const MAX_CELL = 18; // px — keeps sparse heatmaps from ballooning

  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // --- rich hover tooltip (fixed, so it escapes any overflow container) ---
  type Cell = { date: string; total: number; posts: number; comments: number; likes?: number | undefined; roles?: Record<string, number> | undefined } | null;
  type Hover = { day: NonNullable<Cell>; x: number; y: number };
  let hover = $state<Hover | null>(null);
  function enter(e: MouseEvent, day: NonNullable<Cell>): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    hover = { day, x: r.left + r.width / 2, y: r.top };
  }

  function buildWeeks(d: HeatmapDay[]): Cell[][] {
    if (d.length === 0) return [];
    const cells: Cell[] = d.map((x) => ({ date: x.date, total: x.total, posts: x.posts, comments: x.comments, likes: x.likes, roles: x.roles }));
    const firstDow = new Date(d[0]!.date).getUTCDay();
    const padded: Cell[] = Array(firstDow).fill(null).concat(cells);
    const weeks: Cell[][] = [];
    for (let i = 0; i < padded.length; i += 7) weeks.push(padded.slice(i, i + 7));
    return weeks;
  }
  function monthLabels(weeks: Cell[][]): string[] {
    const out: string[] = [];
    let last = -1;
    for (const col of weeks) {
      const first = col.find((c) => c);
      if (!first) {
        out.push('');
        continue;
      }
      const m = new Date(first.date).getUTCMonth();
      out.push(m !== last ? MO[m]! : '');
      last = m;
    }
    return out;
  }

  const weeks = $derived(buildWeeks(days));
  const monthRow = $derived(months && showLabels ? monthLabels(weeks) : []);
</script>

<div class="flex gap-2">
  {#if showLabels}
    <div
      class="flex shrink-0 flex-col font-mono text-[0.55rem] leading-none text-base-content/62 {months ? 'pt-[14px]' : ''}"
      style:gap="{gap}px"
    >
      {#each WD as d, i}<div class="flex flex-1 items-center">{i % 2 === 1 ? d : ''}</div>{/each}
    </div>
  {/if}
  <div class="flex min-w-0 flex-1 flex-col" style:gap="{gap}px">
    {#if monthRow.length}
      <div class="flex" style:gap="{gap}px">
        {#each monthRow as m}<div class="min-w-0 flex-1 overflow-visible whitespace-nowrap font-mono text-[0.55rem] text-base-content/62">{m}</div>{/each}
      </div>
    {/if}
    <div class="flex" style:gap="{gap}px">
      {#each weeks as col}
        <div class="flex min-w-0 flex-1 flex-col" style:gap="{gap}px" style:max-width="{MAX_CELL}px">
          {#each col as day}
            {#if detailed && day}
              {@const clickable = onpick != null && day.total > 0}
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <!-- svelte-ignore a11y_click_events_have_key_events -->
              <div
                class="aspect-square w-full cursor-pointer rounded-[2px] transition-transform hover:z-10 hover:scale-125 {HEAT[heatLevel(day.total, max)]}"
                role={clickable ? 'button' : undefined}
                aria-label={clickable ? `View posts & comments for ${day.date}` : undefined}
                onmouseenter={(e) => enter(e, day)}
                onmouseleave={() => (hover = null)}
                onclick={clickable ? () => onpick?.(day.date) : undefined}
              ></div>
            {:else}
              <div
                class="aspect-square w-full rounded-[2px] {day ? HEAT[heatLevel(day.total, max)] : ''}"
                title={day ? `${day.date}: ${day.total} (${day.posts} posts · ${day.comments} comments)` : ''}
              ></div>
            {/if}
          {/each}
        </div>
      {/each}
    </div>
  </div>
</div>

{#if detailed && hover}
  {@const h = hover}
  <ChartTooltip x={h.x} y={h.y}>
    <div class="font-mono text-[0.7rem] font-semibold">{h.day.date}</div>
    <ActivityBreakdown posts={h.day.posts} comments={h.day.comments} likes={h.day.likes} />
    {#if h.day.roles}
      <div class="mt-2 border-t hair pt-2"><RoleBreakdown roles={h.day.roles} /></div>
    {/if}
    {#if onpick != null && h.day.total > 0}
      <div class="mt-2 text-[0.6rem] font-medium text-primary/80">Click to view posts &amp; comments →</div>
    {/if}
  </ChartTooltip>
{/if}
